import React from "react"

import { AUDIO_SENTIMENT_VALUES } from "@/constants/categorical-metric-values"
import type { PerSegmentReviewConfig } from "@/human-feedback/per-segment"
import { EVENTS, simulationDeepDiveEventBus } from "@/inspection-events"
import { Badge } from "@coval/ui"

import type { AudioRegionParams } from "@/components/audio-player/AudioPlayer"
import type { TestCaseRecord } from "@/types/api/TestCaseRecord"
import type {
  MetricOutputDisplaySource,
  RunMetricOutput,
  SubvalueByTimestamp,
} from "@/types/RunMetricOutput"
import { cn } from "@/utils/cn"

import { AggregateOverviewString } from "../../components/aggregate/AggregateOverviewString"
import { CategoricalMetricRecord } from "../base/CategoricalMetricRecord"
import { WithAudioGateMixin } from "../mixins/WithAudioGateMixin"
import { formatTimeToMinSec } from "../mixins/WithLLMAudioRegionsMixin"

type BaseTestCaseRecord = Pick<
  TestCaseRecord,
  "input_str" | "expected_behaviors" | "expected_output_json"
>

// Color constants for agent audio regions
const AGENT_SENTIMENT_HIGHLIGHT_COLOR = "rgba(24, 144, 255, 0.3)" as const // Blue

// Get color classes for sentiment label
function getSentimentColorClasses(sentimentLabel: string): string {
  const normalized = sentimentLabel?.toLowerCase() || ""

  if (normalized === "happy") {
    return cn("bg-coval-success-surface text-coval-success-on-surface")
  }
  if (normalized === "angry") {
    return cn("bg-coval-danger-surface text-coval-danger-on-surface")
  }
  if (normalized === "sad") {
    return cn(
      "bg-blue-100 text-blue-800",
      "dark:bg-blue-900/30 dark:text-blue-300",
    )
  }
  // Default to neutral (gray/default)
  return cn("bg-surface-2 text-coval-text")
}

// Darker variant for sub-elements (e.g. time box) within the sentiment-colored container
function getSentimentDarkColorClasses(sentimentLabel: string): string {
  const normalized = sentimentLabel?.toLowerCase() || ""

  if (normalized === "happy") {
    return cn("bg-coval-success-surface text-coval-success-on-surface")
  }
  if (normalized === "angry") {
    return cn("bg-coval-danger-surface text-coval-danger-on-surface")
  }
  if (normalized === "sad") {
    return cn(
      "bg-blue-200 text-blue-900",
      "dark:bg-blue-800/50 dark:text-blue-200",
    )
  }
  return cn("bg-surface-2 text-coval-text")
}

function getSentimentBadgeVariant(
  label: string,
): "success" | "danger" | "default" {
  const normalized = label.toLowerCase()
  if (normalized === "happy") {
    return "success"
  }
  if (normalized === "angry") {
    return "danger"
  }
  return "default"
}

export class AudioSentimentMetricRecord extends WithAudioGateMixin(
  CategoricalMetricRecord,
) {
  overviewSize = "large" as const
  unit = "score"
  aggregate_units = "score"

  getCategoricalOptions(): string[] {
    return [...AUDIO_SENTIMENT_VALUES]
  }

  get supportsAudioSegmentReview(): boolean {
    return false
  }

  /**
   * Resolve the effective sentiment label from a value that may use
   * string_value (new) or set_value (legacy).
   */
  private resolveStringValue(value: {
    string_value?: string | null
    set_value?: string[] | null
  }): string | null {
    if (value.string_value) {
      return value.string_value
    }
    if (value.set_value && value.set_value.length > 0) {
      return value.set_value.sort().join(", ")
    }
    return null
  }

  getDisplayValueString(
    mo: MetricOutputDisplaySource | undefined,
  ): string | null {
    if (!mo) {
      return null
    }
    return this.resolveStringValue(mo)
  }

  getHumanReviewDisplayValue(review: {
    output_type: string
    float_value: number | null
    string_value: string | null
    set_value: string[] | null
    json_value: Record<string, unknown> | null
  }): string | null {
    return this.resolveStringValue(review)
  }

  compareValues(
    value1: {
      float_value?: number | null
      string_value?: string | null
      set_value?: string[] | null
    },
    value2: {
      float_value?: number | null
      string_value?: string | null
      set_value?: string[] | null
    },
  ): boolean {
    const a = this.resolveStringValue(value1)?.toLowerCase() ?? null
    const b = this.resolveStringValue(value2)?.toLowerCase() ?? null
    return a === b
  }

  getAudioRegions(
    mo: MetricOutputDisplaySource | undefined,
  ): AudioRegionParams[] {
    if (!mo) {
      return []
    }
    if (mo?.error_message && mo?.error_message !== "") {
      return []
    }
    try {
      const sentiments = mo.subvalues_by_timestamp ?? []
      if (sentiments.length === 0) {
        return []
      }

      return sentiments.map((sentiment, idx) => ({
        id: `sentiment:${idx}`,
        start: sentiment.start_offset,
        end: sentiment.end_offset,
        color: AGENT_SENTIMENT_HIGHLIGHT_COLOR,
        drag: false,
        resize: false,
      }))
    } catch {
      return []
    }
  }

  getPerSegmentReviewConfig(): PerSegmentReviewConfig {
    return {
      labels: [
        { value: "neutral", label: "Neutral", color: "default" },
        { value: "angry", label: "Angry", color: "danger" },
        { value: "happy", label: "Happy", color: "success" },
        { value: "sad", label: "Sad", color: "primary" },
      ],
      buildGroundTruthJson({ values, reviewedCount, totalSegments }) {
        const reviewedEntries = Object.entries(values).filter(
          ([, v]) => v !== null && v !== undefined,
        )
        const labelCounts: Record<string, number> = {}
        for (const [, label] of reviewedEntries) {
          if (label) {
            labelCounts[label] = (labelCounts[label] ?? 0) + 1
          }
        }
        return {
          values_by_segment_key: Object.fromEntries(reviewedEntries),
          total_reviewed: reviewedCount,
          total_segments: totalSegments,
          label_counts: labelCounts,
        }
      },
      buildGroundTruthStringValue({ values }) {
        // Return the mode (most frequent) sentiment label, matching the
        // metric output's string_value which is also the mode.
        const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)
        const labelCounts: Record<string, number> = {}
        for (const label of Object.values(values)) {
          if (label) {
            const capitalized = capitalize(label)
            labelCounts[capitalized] = (labelCounts[capitalized] ?? 0) + 1
          }
        }
        const entries = Object.entries(labelCounts)
        if (entries.length === 0) {
          return null
        }
        // Pick the label with the highest count (mode)
        entries.sort((a, b) => b[1] - a[1])
        return entries[0][0]
      },
    }
  }

  getKeyboardShortcutHints(): { key: string; label: string }[] {
    return [
      { key: "N", label: "Neutral" },
      { key: "A", label: "Angry" },
      { key: "H", label: "Happy" },
      { key: "S", label: "Sad" },
    ]
  }

  _renderSimulationCard(mo: RunMetricOutput): React.ReactNode {
    const value = this.resolveStringValue(mo)
    if (!value) {
      return <div>N/A</div>
    }
    const labels = value.split(", ").filter(Boolean)
    return (
      <div className="flex flex-wrap gap-1 justify-center">
        {labels.map((label) => (
          <Badge key={label} variant={getSentimentBadgeVariant(label)}>
            {label}
          </Badge>
        ))}
      </div>
    )
  }

  renderInDepthRunReview(metricOutputs: RunMetricOutput[]): React.ReactNode {
    if (metricOutputs.length === 0) {
      return (
        <div className="text-coval-text-muted">
          No metric data available for this metric.
        </div>
      )
    }

    const validOutputs = metricOutputs.filter(
      (mo) => mo.string_value !== null && mo.string_value !== undefined,
    )

    if (validOutputs.length === 0) {
      return (
        <div className="text-coval-text-muted">No metric data available.</div>
      )
    }

    return <AggregateOverviewString mos={validOutputs} />
  }

  _renderDeepDive(
    mo: RunMetricOutput,
    testCase: BaseTestCaseRecord,
    _dispatchMessageSelect?: (messageIndex: number | null) => void,
  ): React.ReactNode {
    // Get sentiment data from subvalues_by_timestamp
    const sentiments = mo.subvalues_by_timestamp ?? []

    // Get parent deep dive content (question and explanation)
    const parentContent = super._renderDeepDive(mo, testCase)

    // Handler to seek audio to a specific timestamp
    const handleSeekToTimestamp = (timestamp: number) => {
      simulationDeepDiveEventBus.dispatch(EVENTS.AUDIO_SEEK, {
        time: timestamp,
      })
    }

    if (sentiments.length === 0) {
      return parentContent
    }

    // Helper function to render a sentiment item
    const renderSentimentItem = (
      sentiment: SubvalueByTimestamp,
      idx: number,
    ) => {
      // @deprecated fallback: sentiment_label — remove after subvalues_by_timestamp migration is complete
      const sentimentLabel =
        sentiment.string_value ||
        (sentiment as SubvalueByTimestamp & { sentiment_label?: string })
          .sentiment_label ||
        ""
      const timestampLabel = formatTimeToMinSec(sentiment.start_offset)
      const duration = sentiment.end_offset - sentiment.start_offset
      const durationLabel =
        duration > 0
          ? `${formatTimeToMinSec(sentiment.start_offset)} - ${formatTimeToMinSec(sentiment.end_offset)}`
          : timestampLabel

      return (
        <div
          key={idx}
          className={cn(
            "flex items-start gap-3 p-3 rounded-lg border border-transparent",
            getSentimentColorClasses(sentimentLabel),
            "cursor-pointer transition-colors duration-150",
          )}
          onClick={() => handleSeekToTimestamp(sentiment.start_offset)}
        >
          <div className="shrink-0">
            <div
              className={cn(
                "px-2 py-1 rounded text-xs font-mono",
                getSentimentDarkColorClasses(sentimentLabel),
              )}
            >
              {durationLabel}
            </div>
          </div>
          <div className="flex-1 min-w-0 space-y-1">
            <span className="text-xs font-medium">{sentimentLabel}</span>
          </div>
        </div>
      )
    }

    return (
      <div className="space-y-8">
        {/* Parent content (question and explanation) */}
        {parentContent}

        {/* Sentiments Section */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-coval-text">
            Agent Sentiment Analysis ({sentiments.length})
          </h3>

          {/* Single column layout for agent sentiments */}
          <div className="space-y-2">
            {sentiments.map((sentiment, idx) =>
              renderSentimentItem(sentiment, idx),
            )}
          </div>
        </div>
      </div>
    )
  }
}
